import { Box, Flex, Text, usePrefersReducedMotion } from '@chakra-ui/react'
import { ArrowRight, UserRoundMinus, UserRoundPlus } from 'lucide-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFeatureGate } from '../../hooks/useFeatureGate'
import { useRole } from '../../hooks/auth/useRole'

const LAST_HIRING_ROUTE = 'aboutwrk:people-flow:hiring-route'
const LAST_DEPARTURE_ROUTE = 'aboutwrk:people-flow:departure-route'

const safeStoredRoute = (key: string, prefix: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback
  const stored = window.sessionStorage.getItem(key)
  if (stored && (stored === prefix || stored.startsWith(`${prefix}/`))) return stored
  return fallback
}

/**
 * A human-readable switch between the two halves of the employee lifecycle.
 * Existing inflow/outflow URLs remain canonical so bookmarks, emails, and API
 * cache keys keep working; this component only unifies how people navigate them.
 */
const PeopleFlowSwitcher: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const reduceMotion = usePrefersReducedMotion()
  const { isOrgAdmin, isSuperAdmin } = useRole()
  const { hasFeature } = useFeatureGate()

  const isHiring = location.pathname.startsWith('/user/inflow')
  const isDeparture = location.pathname.startsWith('/user/outflow')
  const canSeeHiring = isSuperAdmin || hasFeature('inflow') || isHiring
  const canSeeDepartures =
    (isOrgAdmin || isSuperAdmin) && (isSuperAdmin || hasFeature('outflow') || isDeparture)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const route = `${location.pathname}${location.search}${location.hash}`
    if (isHiring) window.sessionStorage.setItem(LAST_HIRING_ROUTE, route)
    if (isDeparture) window.sessionStorage.setItem(LAST_DEPARTURE_ROUTE, route)
  }, [isDeparture, isHiring, location.hash, location.pathname, location.search])

  const choices = [
    ...(canSeeHiring
      ? [{
          id: 'hiring',
          label: t('peopleFlow.hiring.title', 'Hiring & onboarding'),
          helper: t('peopleFlow.hiring.helper', 'From candidate to first day'),
          icon: UserRoundPlus,
          active: isHiring,
          path: safeStoredRoute(LAST_HIRING_ROUTE, '/user/inflow', '/user/inflow/candidates'),
        }]
      : []),
    ...(canSeeDepartures
      ? [{
          id: 'departures',
          label: t('peopleFlow.departures.title', 'Departures'),
          helper: t('peopleFlow.departures.helper', 'From notice to final handover'),
          icon: UserRoundMinus,
          active: isDeparture,
          path: safeStoredRoute(LAST_DEPARTURE_ROUTE, '/user/outflow', '/user/outflow/offboarding'),
        }]
      : []),
  ]

  if (choices.length < 2) return null

  return (
    <Flex
      as="nav"
      aria-label={t('peopleFlow.switcherLabel', 'People flow navigation')}
      gap="4px"
      p="5px"
      mb="12px"
      w="100%"
      bg="white"
      border="1px solid"
      borderColor="solid.50"
      borderRadius="13px"
      boxShadow="0 1px 3px rgba(24, 24, 35, 0.04)"
    >
      {choices.map((choice) => {
        const ChoiceIcon = choice.icon
        return (
          <Flex
            key={choice.id}
            as="button"
            type="button"
            aria-current={choice.active ? 'page' : undefined}
            onClick={() => navigate(choice.path)}
            align="center"
            gap="9px"
            minW={{ base: 0, md: '220px' }}
            flex="1"
            px={{ base: '10px', md: '12px' }}
            py="14px"
            textAlign="left"
            borderRadius="10px"
            color={choice.active ? 'primary.100' : 'solid.600'}
            bg={choice.active ? '#F7F6FF' : 'white'}
            border="1px solid"
            borderColor={choice.active ? 'primary.50' : 'transparent'}
            boxShadow="none"
            transition={reduceMotion ? undefined : 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease'}
            _hover={{ bg: choice.active ? 'white' : 'rgba(255, 255, 255, 0.68)', color: 'solid.900' }}
            _focusVisible={{ outline: '3px solid', outlineColor: 'primary.50', outlineOffset: '2px' }}
          >
            <Flex
              boxSize="32px"
              flexShrink={0}
              align="center"
              justify="center"
              borderRadius="9px"
              bg={choice.active ? 'primary.25' : 'solid.25'}
            >
              <ChoiceIcon size={16} strokeWidth={2.1} />
            </Flex>
            <Box minW={0} flex="1">
              <Text fontSize="14px" fontWeight="700" color={choice.active ? 'solid.900' : 'solid.700'} noOfLines={1}>
                {choice.label}
              </Text>
              <Text fontSize="12px" color="solid.400" noOfLines={1}>
                {choice.helper}
              </Text>
            </Box>
            <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
          </Flex>
        )
      })}
    </Flex>
  )
}

export default PeopleFlowSwitcher
